(() => {
  const PAGE_CLASS = 'allco-lead-form-page';
  const ALLOWED_ATTRIBUTION_KEYS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
    'gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid'
  ];

  const safePart = (value, max = 120) => String(value || '')
    .replace(/[\r\n|;]/g, ' ')
    .trim()
    .slice(0, max);

  const buildCampaignReference = () => {
    const source = new URLSearchParams(window.location.search);
    const parts = [];
    ALLOWED_ATTRIBUTION_KEYS.forEach((key) => {
      const value = safePart(source.get(key));
      if (value) parts.push(`${key}=${value}`);
    });
    parts.push(`landing=${safePart(window.location.pathname)}`);
    return parts.join('|').slice(0, 800);
  };

  const validEndpoint = (value) => {
    try {
      const url = new URL(value);
      return url.protocol === 'https:'
        && url.hostname === 'script.google.com'
        && /^\/macros\/s\/[^/]+\/exec$/.test(url.pathname);
    } catch (_error) {
      return false;
    }
  };

  const setStatus = (node, type, message) => {
    if (!node) return;
    node.className = `allco-lead__submit-status allco-lead__submit-status--${type}`;
    node.textContent = message;
    node.hidden = false;
  };

  const syncPageMode = () => {
    document.documentElement.classList.toggle(
      PAGE_CLASS,
      Boolean(document.querySelector('[data-allco-lead]'))
    );
  };

  const initialize = (root) => {
    const form = root.querySelector('[data-allco-direct-form]');
    if (!form || root.dataset.launchReady !== 'true') return;
    if (form.dataset.allcoInitialized === 'true') return;

    const endpoint = root.dataset.webAppUrl;
    if (!validEndpoint(endpoint)) return;
    form.dataset.allcoInitialized = 'true';

    const button = form.querySelector('button[type="submit"]');
    const buttonLabel = button ? button.querySelector('[data-allco-button-label]') : null;
    const status = form.querySelector('[data-allco-submit-status]');
    const errorCall = form.querySelector('[data-allco-error-call]');
    const captchaQuestion = form.querySelector('[data-allco-captcha-question]');
    const captchaAnswer = form.querySelector('[name="captchaAnswer"]');
    const captchaId = form.querySelector('[name="captchaId"]');
    const captchaRefresh = form.querySelector('[data-allco-captcha-refresh]');
    const captchaStatus = form.querySelector('[data-allco-captcha-status]');
    const defaultLabel = buttonLabel ? buttonLabel.textContent : 'Submit request';
    let captchaRequestNumber = 0;

    if (!captchaQuestion || !captchaAnswer || !captchaId || !captchaRefresh || !captchaStatus) return;

    const setCaptchaStatus = (message, isError = false) => {
      captchaStatus.textContent = message;
      captchaStatus.classList.toggle('allco-lead__captcha-status--error', isError);
    };

    const syncSubmitButton = () => {
      if (!button) return;
      const unavailable = form.dataset.submitting === 'true' || form.dataset.captchaReady !== 'true';
      button.disabled = unavailable;
      if (unavailable) button.setAttribute('aria-disabled', 'true');
      else button.removeAttribute('aria-disabled');
    };

    const loadCaptcha = async ({ focus = false } = {}) => {
      const requestNumber = ++captchaRequestNumber;
      form.dataset.captchaReady = 'false';
      captchaId.value = '';
      captchaAnswer.value = '';
      captchaAnswer.disabled = true;
      captchaRefresh.disabled = true;
      captchaQuestion.textContent = 'Loading…';
      setCaptchaStatus('Loading a new question.');
      syncSubmitButton();

      try {
        const challengeUrl = new URL(endpoint);
        challengeUrl.searchParams.set('mode', 'captcha');
        challengeUrl.searchParams.set('_', String(Date.now()));
        const response = await fetch(challengeUrl.toString(), {
          method: 'GET',
          cache: 'no-store',
          credentials: 'omit'
        });
        const result = await response.json();
        const challenge = result && result.captcha;
        const validId = challenge && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(challenge.id || ''));
        const validQuestion = challenge && /^\d{1,2} \+ \d{1,2} = \?$/.test(String(challenge.question || ''));
        if (!response.ok || result.success !== true || !validId || !validQuestion) throw new Error('CAPTCHA_LOAD_FAILED');
        if (requestNumber !== captchaRequestNumber) return false;

        captchaId.value = String(challenge.id);
        captchaQuestion.textContent = String(challenge.question);
        captchaAnswer.disabled = false;
        captchaRefresh.disabled = false;
        form.dataset.captchaReady = 'true';
        setCaptchaStatus('Answer the question above.');
        syncSubmitButton();
        if (focus) captchaAnswer.focus();
        return true;
      } catch (_error) {
        if (requestNumber !== captchaRequestNumber) return false;
        captchaQuestion.textContent = 'Unavailable';
        captchaRefresh.disabled = false;
        setCaptchaStatus('Could not load the question. Select “New question” to try again.', true);
        syncSubmitButton();
        return false;
      }
    };

    captchaRefresh.addEventListener('click', () => {
      if (status) status.hidden = true;
      if (errorCall) errorCall.hidden = true;
      loadCaptcha({ focus: true });
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (form.dataset.captchaReady !== 'true') {
        await loadCaptcha({ focus: true });
        return;
      }
      if (!form.reportValidity() || form.dataset.submitting === 'true') return;
      if (errorCall) errorCall.hidden = true;

      const fields = new FormData(form);
      const payload = {
        service: safePart(fields.get('service'), 80),
        firstName: safePart(fields.get('firstName'), 80),
        zip: safePart(fields.get('zip'), 10),
        phone: safePart(fields.get('phone'), 30),
        email: safePart(fields.get('email'), 254),
        consent: fields.get('consent') === 'yes',
        website: safePart(fields.get('website'), 120),
        captchaId: safePart(fields.get('captchaId'), 64),
        captchaAnswer: safePart(fields.get('captchaAnswer'), 2),
        campaignReference: buildCampaignReference(),
        pageUrl: window.location.href.split('#')[0].slice(0, 500)
      };

      form.dataset.submitting = 'true';
      if (button) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      }
      captchaRefresh.disabled = true;
      if (buttonLabel) buttonLabel.textContent = 'Sending…';
      if (status) status.hidden = true;

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain;charset=utf-8' },
          body: JSON.stringify(payload)
        });

        let result;
        try {
          result = await response.json();
        } catch (_error) {
          throw new Error('INVALID_RESPONSE');
        }
        if (!response.ok || !result || result.success !== true) {
          const submissionError = new Error(result && result.error ? result.error : 'SUBMISSION_FAILED');
          submissionError.code = result && result.error ? String(result.error) : 'SUBMISSION_FAILED';
          throw submissionError;
        }

        setStatus(
          status,
          'success',
          result.duplicate
            ? 'We already received this request. No need to submit it again.'
            : 'Thanks—your request was received.'
        );
        form.reset();
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'allco_lead_submit_success',
          form_id: 'allco_offer_form',
          service: payload.service,
          campaign_reference_present: Boolean(payload.campaignReference),
          duplicate: Boolean(result.duplicate)
        });
        await loadCaptcha();
      } catch (error) {
        const code = String(error && error.code ? error.code : 'SUBMISSION_FAILED');
        const captchaError = /^CAPTCHA_/.test(code);
        const captchaMessage = code === 'CAPTCHA_INCORRECT'
          ? 'That answer was not correct. Please answer the new question.'
          : 'The security question expired. Please answer the new question.';
        setStatus(status, 'error', captchaError ? captchaMessage : 'We could not send your request. Try again or call the local shop.');
        if (errorCall) errorCall.hidden = captchaError;
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'allco_lead_submit_error', reason: captchaError ? 'security_check' : 'submission' });
        await loadCaptcha({ focus: captchaError });
      } finally {
        form.dataset.submitting = 'false';
        if (button) {
          button.removeAttribute('aria-busy');
        }
        if (buttonLabel) buttonLabel.textContent = defaultLabel;
        syncSubmitButton();
      }
    });

    loadCaptcha();
  };

  const initializeAll = (scope = document) => {
    syncPageMode();
    scope.querySelectorAll('[data-allco-lead]').forEach(initialize);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', () => window.setTimeout(syncPageMode, 0));
})();
